import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, PhoneIcon } from '@heroicons/react/24/solid';
import { 
  WrenchScrewdriverIcon, 
  BuildingOfficeIcon, 
  ScaleIcon, 
  TruckIcon,
  PaintBrushIcon,
  HomeModernIcon,
  CameraIcon,
  FireIcon,
  BoltIcon,
  SparklesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  
  // Form state for quote request
  const [quoteFormData, setQuoteFormData] = React.useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    budget: '',
    message: ''
  });
  const [quoteLoading, setQuoteLoading] = React.useState(false);
  const [quoteError, setQuoteError] = React.useState<string | null>(null);
  const [quoteSuccess, setQuoteSuccess] = React.useState(false);
  
  // Hero carousel images sourced from the latest client-provided set
  const heroImages = [
    '/images/home/hero/IMG-20260213-WA0003.jpg',
    '/images/home/hero/IMG-20260305-WA0011.jpg',
    '/images/home/hero/IMG-20260305-WA0012.jpg',
    '/images/home/hero/IMG-20260305-WA0013.jpg',
    '/images/home/hero/IMG-20260305-WA0017.jpg',
    '/images/home/hero/IMG-20260305-WA0018.jpg',
    '/images/home/hero/IMG-20260305-WA0019.jpg',
    '/images/home/hero/IMG-20260305-WA0020.jpg'
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const handleQuoteFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setQuoteFormData({
      ...quoteFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setQuoteLoading(true);
    setQuoteError(null);
    setQuoteSuccess(false);

    try {
      const response = await fetch('https://plencoplumbingltd.com/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: quoteFormData.name,
          email: quoteFormData.email,
          phone: quoteFormData.phone,
          service: quoteFormData.service,
          budget: quoteFormData.budget,
          message: quoteFormData.message
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit quote request');
      }

      setQuoteSuccess(true);
      setQuoteFormData({
        name: '',
        phone: '',
        email: '',
        service: '',
        budget: '',
        message: ''
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => setQuoteSuccess(false), 5000);
    } catch (err) {
      setQuoteError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setQuoteLoading(false);
    }
  };

  const features = [
    { title: 'Expert Team', description: 'Professional and experienced technicians' },
    { title: 'Trusted Services', description: 'Quality work guaranteed since 2013' },
    { title: '24/7 Support', description: 'Round-the-clock emergency services' },
  ];

  const mainServices = [
    { 
      title: 'General Services', 
      description: 'General plumbing, roofing, electrical systems, and general construction',
      image: '/images/gallery/37.png',
      items: ['General Plumbing', 'Roofing Systems', 'Electrical Wiring', 'General Construction']
    },
    { 
      title: 'Calibration Services', 
      description: 'Tank calibration, fuel station construction, and specialized measurements',
      image: '/images/gallery/35.png',
      items: ['Tank Calibration', 'Fuel Station Construction', 'Technical Measurements', 'Quality Control']
    },
    { 
      title: 'Other Services', 
      description: 'Renovation, landscaping, CCTV installation, and specialized systems',
      image: '/images/gallery/32.png',
      items: ['Renovation Works', 'Landscaping Design', 'CCTV Installation', 'Biogas Systems']
    }
  ];

  const featuredProjects = [
    {
      title: 'Kitchen Cabinet Installation',
      description: 'Professional kitchen cabinet installation and countertop fitting for modern homes',
      image: '/images/home/WhatsApp-Image-2022-05-19-at-11.04.19-AM.jpeg',
      location: 'Kampala, Uganda'
    },
    {
      title: 'Quality Workmanship',
      description: 'Professional team delivering excellence across all construction and plumbing projects',
      image: '/images/home/WhatsApp-Image-2022-05-19-at-11.04.19-AM.jpeg',
      location: 'Kampala, Uganda'
    },
    {
      title: 'Professional Services',
      description: 'Comprehensive solutions for residential, commercial, and specialized construction needs',
      image: '/images/home/WhatsApp-Image-2022-05-19-at-11.04.19-AM.jpeg',
      location: 'Uganda'
    }
  ];

  const services = [
    { title: 'General Plumbing', icon: WrenchScrewdriverIcon },
    { title: 'Calibration Services', icon: ScaleIcon },
    { title: 'Fuel Station Construction', icon: TruckIcon },
    { title: 'Tank Fabrication & Designs', icon: BuildingOfficeIcon },
    { title: 'Painting & Renovation', icon: PaintBrushIcon },
    { title: 'Architectural Designing', icon: HomeModernIcon },
    { title: 'House Roofing Systems', icon: HomeModernIcon },
    { title: 'Swimming Pool Design', icon: SparklesIcon },
    { title: 'CCTV & Electrical Wiring', icon: CameraIcon },
    { title: 'Biogas Systems', icon: FireIcon },
    { title: 'Automatic Gates & Alarms', icon: BoltIcon },
    { title: 'Cleaning Services', icon: SparklesIcon },
  ];

  // Client logos images are missing from the backup (2022/04 folder not included)
  // Commenting out until images are available
  // const clientLogos = [
  //   { src: '/images/2022/04/DHL-international-Ltd-150x150.png', alt: 'DHL International Ltd' },
  //   { src: '/images/2022/04/Marie-Stops-Uganda-150x150.png', alt: 'Marie Stops Uganda' },
  //   { src: '/images/2022/04/National-Water-and-Sewerage-Cooperation-NWSC-150x150.jpg', alt: 'NWSC' },
  //   { src: '/images/2022/04/Uganda-National-Roads-Authority-UNRA-150x150.png', alt: 'UNRA' },
  //   { src: '/images/2022/04/White-castle-hotel-150x150.png', alt: 'White Castle Hotel' },
  //   { src: '/images/2022/04/Tiva-Waters-Uganda-150x130.png', alt: 'Tiva Waters' },
  // ];

  return (
    <div className="bg-white">
      <SEO 
        title="Home - Plenco Plumbing & Construction Co. Ltd | Leading Construction Company in Uganda"
        description="Plenco Plumbing & Construction Co. Ltd - Multi construction and plumbing company serving East Africa since 2013. Expert construction services, plumbing, tank calibration, renovation works, and general contracts. Available 24/7 in Kampala, Uganda. Contact: +256 200 906397 | +256 704 485986 | +256 786 272003"
        keywords="construction services Uganda, construction company Kampala, construction and plumbing East Africa, tank calibration Uganda, building construction Kampala, renovation works Uganda, general plumbing services, fuel station construction, 24/7 emergency plumbing Uganda, licensed plumbers Kampala"
        canonicalUrl="http://www.plencoplumbingltd.com/"
        ogImage="/images/gallery/cropped-Logo-1.png"
        type="website"
      />
      
      {/* Hero Section with Image Carousel */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt={`Plenco Project ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwMCIgaGVpZ2h0PSI4MDAiIHZpZXdCb3g9IjAgMCAxMjAwIDgwMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwMCIgaGVpZ2h0PSI4MDAiIGZpbGw9IiMzNDczQjYiLz48dGV4dCB4PSI2MDAiIHk9IjQwMCIgZmlsbD0id2hpdGUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSI0OCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlBsZW5jbyBQcm9qZWN0PC90ZXh0Pjwvc3ZnPg==';
                }}
              />
              <div className="absolute inset-0 bg-primary-900/45"></div>
            </div>
          ))}
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8 leading-tight">
            Plenco Plumbing & Construction Co. Ltd
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-6 sm:mb-8 text-white">
            Multi Construction and Plumbing Company in East Africa
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link
              to="/services"
              className="bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Our Services
            </Link>
            <Link
              to="/contact"
              className="bg-transparent text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-all duration-200 border-2 border-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Get Quote
            </Link>
          </div>
        </div>
        
        {/* Slide Indicators */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 z-10">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-8 sm:py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-3xl sm:text-4xl font-black mb-2">
              <span className="text-primary">Why Choose</span>{' '}
              <span className="text-gray-900">Us</span>
            </h2>
            <p className="text-sm sm:text-base text-gray-600">Our clients are at the centre of our service</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => {
              const colors = [
                { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'bg-blue-500' },
                { bg: 'bg-green-50', text: 'text-green-600', icon: 'bg-green-500' },
                { bg: 'bg-orange-50', text: 'text-orange-600', icon: 'bg-orange-500' }
              ];
              const color = colors[index];
              return (
                <div key={index} className={`${color.bg} rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 p-6`}>
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 ${color.icon} rounded-lg p-3`}>
                      <CheckCircleIcon className="h-6 w-6 text-white flex-shrink-0" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-12 bg-gradient-to-r from-primary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="mb-4">
                <span className="inline-block bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold mb-2">ABOUT PLENCO</span>
                <h3 className="text-3xl font-black mb-2">
                  <span className="text-primary">Trusted Service</span>{' '}
                  <span className="text-gray-900">Provider Since 2013</span>
                </h3>
              </div>
              <p className="text-gray-600 text-base mb-3 leading-relaxed">
                We are a limited Liability Company based in Kampala, Uganda, specialized in plumbing, 
                tank calibration, general engineering, building construction, renovation works and general contracts.
              </p>
              <p className="text-gray-600 text-base mb-3 leading-relaxed">
                Also engaged in Real estate development, general equipments, merchants, land developers, 
                commission agents and other business.
              </p>
              <p className="text-gray-600 text-base mb-4 leading-relaxed">
                The company has grown over the past years in delivering excellent services in plumbing 
                and construction industry and now operates within Uganda and East Africa.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 bg-primary hover:bg-primary-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
              >
                <span>LEARN MORE</span>
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="/images/gallery/37.png"
                alt="Plenco Management Team"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div class="bg-gray-300 rounded-lg shadow-lg h-96 flex items-center justify-center"><div class="text-center text-gray-600"><svg class="mx-auto h-24 w-24 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg><p class="mt-2 text-sm">Team Photo</p></div></div>';
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section with Image */}
      <section className="bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 mb-6">
            <div>
              <h2 className="text-3xl font-black mb-2">
                <span className="text-primary">What</span> We Do
              </h2>
              <p className="text-gray-600 text-sm font-semibold mb-4">Trusted Service Provider Since 2013</p>
              <p className="text-gray-600 text-base mb-6 leading-relaxed">
                General construction services and plumbing, engineering, and specialized installations across Uganda and East Africa.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {services.slice(0, 12).map((service, index) => {
                  const colors = [
                    'bg-blue-50',
                    'bg-green-50',
                    'bg-orange-50',
                    'bg-purple-50',
                    'bg-pink-50',
                    'bg-indigo-50',
                    'bg-cyan-50',
                    'bg-amber-50',
                    'bg-emerald-50',
                    'bg-rose-50',
                    'bg-sky-50',
                    'bg-teal-50'
                  ];
                  return (
                    <div key={index} className={`flex items-center space-x-2 ${colors[index % colors.length]} p-3 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all`}>
                      <div className="bg-primary rounded-lg p-1.5">
                        <service.icon className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{service.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services - Three Main Categories */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-black mb-2">
              <span className="text-primary">Our</span> Services
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Three main categories of professional services to meet all your construction and engineering needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mainServices.map((service, index) => {
              const cardColors = ['border-t-4 border-t-blue-500', 'border-t-4 border-t-green-500', 'border-t-4 border-t-orange-500'];
              return (
                <div key={index} className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 group ${cardColors[index]}`}>
                  <div className="relative h-48">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiMzNDczQjYiLz48cGF0aCBkPSJNMTUwIDEwMEgyNTBIMzAwSDI1MEgxNTBaIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjMiIGZpbGw9Im5vbmUiLz48dGV4dCB4PSIyMDAiIHk9IjE4MCIgZmlsbD0id2hpdGUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U2VydmljZSBJbWFnZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-primary-900 opacity-10 group-hover:opacity-20 transition-opacity"></div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">{service.description}</p>
                    <ul className="space-y-1.5 mb-3">
                      {service.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center space-x-2 text-xs text-gray-600">
                          <CheckCircleIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/services"
                      className="inline-block text-primary font-medium text-sm hover:text-primary-700 transition-colors"
                    >
                      Learn More →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/projects"
              className="inline-block bg-primary text-white px-6 py-2.5 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              VIEW ALL PROJECTS
            </Link>
          </div>
        </div>
      </section>

      {/* Some of Our Projects */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-2">
              <span className="text-primary">Some of Our</span> <span className="text-gray-600">Projects</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Showcasing our latest completed projects across construction, plumbing, and specialized services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Project 1 */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="/images/gallery/IMG_00000168-Copy.jpg"
                  alt="Professional Construction Services"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNMTAwIDEwMEwyMDAgNTBMMzAwIDEwMEwzMDAgMjAwTDIwMCAyNTBMMTAwIDIwMFoiIHN0cm9rZT0iI0M3RDJFMCIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+PHRleHQgeD0iMjAwIiB5PSIxNjAiIGZpbGw9IiM2Njc2ODgiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q29uc3RydWN0aW9uPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Professional Construction Services</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Expert construction and structural work delivering quality finishes and durability.</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-700 transition-colors group/link"
                >
                  <span>View Project</span>
                  <ArrowRightIcon className="h-4 w-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Project 2 */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="/images/gallery/IMG_20221109_125821-1.jpg"
                  alt="Expert Plumbing Solutions"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiNFNUU3RUIiLz48Y2lyY2xlIGN4PSIxNTAiIGN5PSIxMjAiIHI9IjMwIiBzdHJva2U9IiNDN0QyRTAiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjxyZWN0IHg9IjEwMCIgeT0iMTcwIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjYwIiBzdHJva2U9IiNDN0QyRTAiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjx0ZXh0IHg9IjIwMCIgeT0iMjQwIiBmaWxsPSIjNjY3Njg4IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlBsdW1iaW5nPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Expert Plumbing Solutions</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Professional plumbing installation, repairs, and maintenance for residential and commercial properties.</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-700 transition-colors group/link"
                >
                  <span>View Project</span>
                  <ArrowRightIcon className="h-4 w-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Project 3 */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="/images/gallery/IMG_20230914_133609_489-1.jpg"
                  alt="Tank Calibration Services"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiNFNUU3RUIiLz48cmVjdCB4PSI1MCIgeT0iNTAiIHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBzdHJva2U9IiNDN0QyRTAiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjx0ZXh0IHg9IjIwMCIgeT0iMTYwIiBmaWxsPSIjNjY3Njg4IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkNhbGlicmF0aW9uPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Tank Calibration Services</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Precision tank calibration and measurement services for industrial and commercial applications.</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-700 transition-colors group/link"
                >
                  <span>View Project</span>
                  <ArrowRightIcon className="h-4 w-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Project 4 */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="/images/gallery/WhatsApp-Image-2022-05-16-at-5.45.23-PM-768x768.jpeg"
                  alt="Building Renovation"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiNFNUU3RUIiLz48cG9seWdvbiBwb2ludHM9IjEwMCwxMDAgMzAwLDEwMCAzMDAsMjAwIDEwMCwyMDAiIHN0cm9rZT0iI0M3RDJFMCIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+PHRleHQgeD0iMjAwIiB5PSIxNjAiIGZpbGw9IiM2Njc2ODgiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+UmVub3ZhdGlvbjwvdGV4dD48L3N2Zz4=';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Building Renovation</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Complete renovation and restoration services transforming properties with modern improvements and quality finishes.</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-700 transition-colors group/link"
                >
                  <span>View Project</span>
                  <ArrowRightIcon className="h-4 w-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Project 5 */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="/images/gallery/IMG_20230901_131741_1-1.jpg"
                  alt="Specialized Engineering"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiNFNUU3RUIiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSIxNTAiIHI9IjUwIiBzdHJva2U9IiNDN0QyRTAiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjx0ZXh0IHg9IjIwMCIgeT0iMTYwIiBmaWxsPSIjNjY3Njg4IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkVuZ2luZWVyaW5nPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Specialized Engineering</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Biogas systems, automatic gates, CCTV installation, and other specialized technical solutions tailored to your needs.</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-700 transition-colors group/link"
                >
                  <span>View Project</span>
                  <ArrowRightIcon className="h-4 w-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Project 6 */}
            <div className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="/images/gallery/Fire-Safety.jpg"
                  alt="Fire Safety Systems"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNMjAwIDUwTDI1MCAxNTBMMjAwIDEzMEwxNTAgMTUwWiIgZmlsbD0iIkZGNDQwMCIvPjxyZWN0IHg9IjE3MCIgeT0iMTYwIiB3aWR0aD0iNjAiIGhlaWdodD0iODAiIHN0cm9rZT0iI0M3RDJFMCIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+PHRleHQgeD0iMjAwIiB5PSIyODAiIGZpbGw9IiM2Njc2ODgiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+RmlyZSBTYWZldHk8L3RleHQ+PC9zdmc+';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Fire Safety Systems</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Professional fire safety installation and emergency response systems for commercial and residential properties.</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-700 transition-colors group/link"
                >
                  <span>View Project</span>
                  <ArrowRightIcon className="h-4 w-4 ml-2 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          {/* Call to Action for all projects */}
          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg mb-6">Want to see more? Explore our complete project portfolio</p>
            <Link
              to="/projects"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl"
            >
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      {/* Some of Our Featured Projects - OLD SECTION, KEPT FOR REFERENCE */}

      {/* Emergency Plumbing Service Section */}
      <section className="bg-gradient-to-r from-primary to-primary-600 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-black mb-3 leading-tight">
                Emergency Plumbing Service
              </h2>
              <h3 className="text-3xl md:text-4xl font-bold mb-6 text-primary-100">
                24 Hours 7 Days a Week
              </h3>
              <p className="text-lg md:text-xl mb-8 text-primary-100 leading-relaxed">
                Quality Plumbing Services from a Team of Professionals
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-white text-primary px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl text-lg group"
              >
                <PhoneIcon className="h-6 w-6 group-hover:scale-110 transition-transform" />
                <span>Contact Us Now</span>
              </Link>
            </div>

            {/* Right Stats/Info */}
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 rounded-full p-3 flex-shrink-0">
                    <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">Quick Response</h4>
                    <p className="text-primary-100">Available anytime, day or night for urgent plumbing emergencies</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 rounded-full p-3 flex-shrink-0">
                    <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">Professional Team</h4>
                    <p className="text-primary-100">Experienced professionals ready to handle any plumbing challenge</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 rounded-full p-3 flex-shrink-0">
                    <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">Quality Service</h4>
                    <p className="text-primary-100">Trusted by over 500 satisfied clients across East Africa</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request For a Quote */}
      <section className="py-20 bg-gradient-to-r from-primary-50 via-blue-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-2">
              <span className="text-primary">Request For a</span> <span className="text-gray-600">Quote</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Get a professional assessment and competitive pricing for your construction, plumbing, and engineering projects
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Form Section - Left Side */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-t-primary h-full flex flex-col">
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Send Your Details</h3>
              <form onSubmit={handleQuoteSubmit} className="space-y-4 flex-1 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Name *</label>
                      <input 
                        type="text" 
                        name="name"
                        value={quoteFormData.name}
                        onChange={handleQuoteFormChange}
                        required
                        className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all text-sm" 
                        placeholder="Full Name" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Phone *</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={quoteFormData.phone}
                        onChange={handleQuoteFormChange}
                        required
                        className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all text-sm" 
                        placeholder="+256 XXX" 
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                    <input 
                      type="email" 
                      name="email"
                      value={quoteFormData.email}
                      onChange={handleQuoteFormChange}
                      required
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all text-sm" 
                      placeholder="email@example.com" 
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Service *</label>
                      <select 
                        name="service"
                        value={quoteFormData.service}
                        onChange={handleQuoteFormChange}
                        required
                        className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all text-sm"
                      >
                        <option value="">Select service</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="construction">Construction</option>
                        <option value="calibration">Calibration</option>
                        <option value="renovation">Renovation</option>
                        <option value="fire-safety">Fire Safety</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Budget</label>
                      <select 
                        name="budget"
                        value={quoteFormData.budget}
                        onChange={handleQuoteFormChange}
                        className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all text-sm"
                      >
                        <option value="">Select range</option>
                        <option value="under-1m">Under 1M</option>
                        <option value="1m-5m">1M - 5M</option>
                        <option value="5m-10m">5M - 10M</option>
                        <option value="10m-50m">10M - 50M</option>
                        <option value="above-50m">Above 50M</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Project Details *</label>
                    <textarea 
                      name="message"
                      value={quoteFormData.message}
                      onChange={handleQuoteFormChange}
                      rows={3} 
                      required
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all resize-none text-sm" 
                      placeholder="Describe your project..."
                    ></textarea>
                  </div>
                </div>

                <div>
                  {quoteSuccess && (
                    <div className="p-3 mb-3 bg-green-50 border-2 border-green-500 rounded-lg text-green-700 font-semibold text-center text-sm">
                      Quote request received! We'll contact you within 24 hours.
                    </div>
                  )}
                  
                  {quoteError && (
                    <div className="p-3 mb-3 bg-red-50 border-2 border-red-500 rounded-lg text-red-700 font-semibold text-center text-sm">
                      {quoteError}
                    </div>
                  )}
                  
                  <button 
                    type="submit"
                    disabled={quoteLoading}
                    className="w-full bg-primary text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {quoteLoading ? 'Sending...' : 'Request Quote'}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">Response within 24 hours</p>
                </div>
              </form>
            </div>

            {/* Details & Directions Section - Right Side */}
            <div className="h-full flex flex-col space-y-4">
              {/* Location Card */}
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-l-primary flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-4">📍 Our Location</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">KIU World Saloon Building</span><br/>
                      Basement Floor, Room B05<br/>
                      Kansanga, Ggaba Rd<br/>
                      P.O Box 148303<br/>
                      <span className="font-semibold text-primary">Kampala, Uganda</span>
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">CONTACT INFO</p>
                    <p className="font-semibold text-gray-900 mb-1">Phone</p>
                    <p className="text-primary font-bold mb-3">+256 200 906397</p>
                    <p className="text-xs text-gray-600 mb-3">+256 704 485986 | +256 786 272003</p>
                    <p className="font-semibold text-gray-900 mb-1">Email</p>
                    <p className="text-primary font-bold">plencoplumbing@gmail.com</p>
                  </div>
                </div>
              </div>

              {/* Benefits Card */}
              <div className="bg-gradient-to-br from-primary to-primary-600 text-white p-6 rounded-lg shadow-lg flex-1">
                <h4 className="font-bold mb-3 text-sm">✓ Why Choose Us?</h4>
                <ul className="space-y-2 text-xs">
                  <li className="flex items-start space-x-2">
                    <span className="text-primary-100 flex-shrink-0 mt-0.5">→</span>
                    <span>Free professional assessment</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary-100 flex-shrink-0 mt-0.5">→</span>
                    <span>Transparent pricing</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary-100 flex-shrink-0 mt-0.5">→</span>
                    <span>Expert recommendations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-primary-100 flex-shrink-0 mt-0.5">→</span>
                    <span>Clear project timeline</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-primary-300 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span>Completed Projects:</span>
                    <span className="font-bold text-lg">500+</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>Experience:</span>
                    <span className="font-bold text-lg">10+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section - DISABLED: Images missing from backup */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Some of Our Main Clients</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {clientLogos.map((client, index) => (
              <div key={index} className="flex items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <img
                  src={client.src}
                  alt={client.alt}
                  className="max-h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Client Names Section - Text Only Version */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-2">
              <span className="text-primary">Some of Our</span> <span className="text-gray-600">Main Clients</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Trusted by leading organizations across East Africa
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {[
              { name: 'DHL International', img: '/images/home/DHL-international-Ltd-150x150.png' },
              { name: 'Marie Stops Uganda', img: '/images/home/Marie-Stops-Uganda-150x150.png' },
              { name: 'Namulondo Investments', img: '/images/home/Namulondo-Investments-Limited-150x150.png' },
              { name: 'NWSC', img: '/images/home/National-Water-and-Sewerage-Cooperation-.jpg' },
              { name: 'Spedag Interfreight', img: '/images/home/Spedag-Interfreight-k-Ltd-150x150.jpg' },
              { name: 'Tiva Waters', img: '/images/home/Tiva-Waters-Uganda-150x130.png' },
              { name: 'UNRA', img: '/images/home/Uganda-National-Roads-Authority-UNRA-150x150.png' },
              { name: 'White Castle Hotel', img: '/images/home/White-castle-hotel-150x150.png' }
            ].map((client, index) => (
              <div key={index} className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg hover:from-primary hover:to-primary-600 hover:text-white transition-all duration-300 shadow-sm hover:shadow-lg border border-gray-200 hover:border-primary group cursor-pointer">
                <img 
                  src={client.img}
                  alt={client.name}
                  className="h-14 w-14 object-contain mb-3 group-hover:brightness-200 transition-all"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <span className="text-xs font-bold text-center text-gray-700 group-hover:text-white transition-colors">{client.name}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-primary-50 to-blue-50 p-8 rounded-lg border border-primary-200">
            <p className="text-center text-gray-700">
              Join <span className="font-bold text-primary">500+ satisfied clients</span> who trust Plenco for their construction, plumbing, and engineering needs
            </p>
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black text-gray-900 mb-2">
              <span className="text-primary">Our</span> <span className="text-gray-600">Location</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Visit us at KIU World Saloon Building, Basement Floor Room B05
            </p>
            <p className="text-gray-600">
              Kansanga, Ggaba Rd, P.O Box 148303, Kampala, Uganda
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=0.2944772,32.6046877&z=17&output=embed"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: '0.5rem' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-primary rounded-full p-3">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Phone</h3>
                    <p className="text-gray-600">+256 200 906397 | +256 704 485986 | +256 786 272003</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-primary rounded-full p-3">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Email</h3>
                    <p className="text-gray-600">plencoplumbing@gmail.com</p>
                  </div>
                </div>
              </div>
              <div className="bg-primary text-white p-6 rounded-lg text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">PLENCO</h3>
                <p className="text-primary-100">Plumbing & Construction Co. Ltd</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-primary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-primary">
              <div className="text-5xl font-black text-primary mb-2">500+</div>
              <div className="text-gray-700 font-semibold">Happy Clients</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-blue-500">
              <div className="text-5xl font-black text-blue-600 mb-2">10+</div>
              <div className="text-gray-700 font-semibold">Years Experience</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-green-500">
              <div className="text-5xl font-black text-green-600 mb-2">36+</div>
              <div className="text-gray-700 font-semibold">Team Members</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-orange-500">
              <div className="text-5xl font-black text-orange-600 mb-2">4</div>
              <div className="text-gray-700 font-semibold">Countries</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
